/**
 * Dedicated IndexedDB for user custom indicators (v2: projects + files).
 * Opens a connection per operation and closes immediately — no persistent cache.
 */

import { openDB, type IDBPDatabase, type IDBPTransaction } from "idb";
import type {
  CustomIndicatorBuild,
  CustomIndicatorProject,
  CustomIndicatorRecord,
  ProjectFileNode,
} from "./types";
import { byteSize, checkLimits } from "./limits";
import {
  defaultUserProjectOwnership,
  normalizeProject,
} from "./project-ownership";
import {
  basename,
  collectDescendantPaths,
  computeProjectSizeBytes,
  DEFAULT_ENTRY_FILE,
  DEFAULT_README_FILE,
  fileId,
  getDefaultProjectFiles,
  getEntryContent,
  joinPath,
  movePath,
  normalizePath,
  renamePath,
  validatePath,
} from "./project-fs";

const DB_NAME = "VybeCustomIndicatorsDB";
const DB_VERSION = 4;
const LEGACY_STORE = "indicators";
const PROJECTS_STORE = "projects";
const FILES_STORE = "files";
const BUILDS_STORE = "builds";

type CustomIndicatorsDb = IDBPDatabase<{
  indicators: { key: string; value: CustomIndicatorRecord };
  projects: { key: string; value: CustomIndicatorProject };
  files: {
    key: string;
    value: ProjectFileNode;
    indexes: { byProject: string };
  };
  builds: { key: string; value: CustomIndicatorBuild };
}>;

async function openCustomIndicatorsDb(): Promise<CustomIndicatorsDb> {
  return openDB(DB_NAME, DB_VERSION, {
    async upgrade(db, oldVersion, _newVersion, transaction) {
      if (!db.objectStoreNames.contains(LEGACY_STORE)) {
        db.createObjectStore(LEGACY_STORE, { keyPath: "id" });
      }

      if (!db.objectStoreNames.contains(PROJECTS_STORE)) {
        db.createObjectStore(PROJECTS_STORE, { keyPath: "id" });
      }

      if (!db.objectStoreNames.contains(FILES_STORE)) {
        const filesStore = db.createObjectStore(FILES_STORE, { keyPath: "id" });
        filesStore.createIndex("byProject", "projectId");
      }

      if (!db.objectStoreNames.contains(BUILDS_STORE)) {
        db.createObjectStore(BUILDS_STORE, { keyPath: "projectId" });
      }

      if (oldVersion < 2) {
        await migrateV1ToV2(transaction);
      }
      if (oldVersion < 4) {
        await migrateV3ToV4(transaction);
      }
    },
  });
}

async function migrateV3ToV4(
  transaction: IDBPTransaction<
    CustomIndicatorsDb extends IDBPDatabase<infer S> ? S : never,
    ("indicators" | "projects" | "files" | "builds")[],
    "versionchange"
  >
): Promise<void> {
  const projectsStore = transaction.objectStore(PROJECTS_STORE);
  const projects = (await projectsStore.getAll()) as CustomIndicatorProject[];
  for (const project of projects) {
    const normalized = normalizeProject(project);
    if (
      project.owner !== normalized.owner ||
      project.source !== normalized.source ||
      project.readOnly !== normalized.readOnly ||
      project.hasSource !== normalized.hasSource
    ) {
      await projectsStore.put(normalized);
    }
  }
}

async function migrateV1ToV2(
  transaction: IDBPTransaction<
    CustomIndicatorsDb extends IDBPDatabase<infer S> ? S : never,
    ("indicators" | "projects" | "files" | "builds")[],
    "versionchange"
  >
): Promise<void> {
  const legacyStore = transaction.objectStore(LEGACY_STORE);
  const projectsStore = transaction.objectStore(PROJECTS_STORE);
  const filesStore = transaction.objectStore(FILES_STORE);
  const now = Date.now();

  const records = (await legacyStore.getAll()) as unknown as CustomIndicatorRecord[];

  for (const record of records) {
    const projectId = record.id;
    const entryContent = record.code ?? "";
    const entryBytes = byteSize(entryContent);
    const readmeContent = `# ${record.name}\n\nCustom Vybe indicator (C# / Vybe.Indicators SDK).\n`;
    const readmeBytes = byteSize(readmeContent);

    const project: CustomIndicatorProject = {
      id: projectId,
      name: record.name,
      version: record.version,
      description: "",
      overlay: record.overlay,
      tags: ["custom", "csharp"],
      kind: "indicator",
      entryFilePath: DEFAULT_ENTRY_FILE,
      csprojFilePath: "Indicator.csproj",
      sizeBytes: entryBytes + readmeBytes,
      entryContent,
      ...defaultUserProjectOwnership(),
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };

    await projectsStore.put(project);

    const entryFile: ProjectFileNode = {
      id: fileId(projectId, DEFAULT_ENTRY_FILE),
      projectId,
      path: DEFAULT_ENTRY_FILE,
      type: "file",
      content: entryContent,
      sizeBytes: entryBytes,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };
    await filesStore.put(entryFile);

    const readmeFile: ProjectFileNode = {
      id: fileId(projectId, DEFAULT_README_FILE),
      projectId,
      path: DEFAULT_README_FILE,
      type: "file",
      content: readmeContent,
      sizeBytes: readmeBytes,
      createdAt: now,
      updatedAt: now,
    };
    await filesStore.put(readmeFile);
  }
}

export function createNewIndicatorId(): string {
  return `ci_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function projectToRecord(
  project: CustomIndicatorProject,
  entryContent: string
): CustomIndicatorRecord {
  return {
    id: project.id,
    name: project.name,
    version: project.version,
    code: entryContent,
    overlay: project.overlay,
    iconFilePath: project.iconFilePath,
    iconDataUrl: project.iconDataUrl,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
  };
}

// --- Project CRUD ---

export async function listProjects(): Promise<CustomIndicatorProject[]> {
  const db = await openCustomIndicatorsDb();
  try {
    const rows = await db.getAll(PROJECTS_STORE);
    const normalized = rows.map((row) => normalizeProject(row));
    normalized.sort((a, b) => b.updatedAt - a.updatedAt);
    return normalized;
  } finally {
    db.close();
  }
}

export async function findProjectBySourceTemplateId(
  templateId: string
): Promise<CustomIndicatorProject | undefined> {
  const projects = await listProjects();
  return projects.find((p) => p.sourceTemplateId === templateId);
}

export async function saveProjectRecord(
  project: CustomIndicatorProject
): Promise<void> {
  const db = await openCustomIndicatorsDb();
  try {
    project.updatedAt = Date.now();
    await db.put(PROJECTS_STORE, normalizeProject(project));
  } finally {
    db.close();
  }
}

/** Legacy adapter: list as CustomIndicatorRecord for tab/chart consumers */
export async function listCustomIndicators(): Promise<CustomIndicatorRecord[]> {
  const projects = await listProjects();
  return projects.map((p) =>
    projectToRecord(p, p.entryContent ?? "")
  );
}

export async function getProject(
  id: string
): Promise<CustomIndicatorProject | undefined> {
  const db = await openCustomIndicatorsDb();
  try {
    const row = await db.get(PROJECTS_STORE, id);
    return row ? normalizeProject(row) : undefined;
  } finally {
    db.close();
  }
}

/** Legacy adapter */
export async function getCustomIndicator(
  id: string
): Promise<CustomIndicatorRecord | undefined> {
  const project = await getProject(id);
  if (!project) return undefined;
  let entryContent = project.entryContent ?? "";
  if (!entryContent) {
    const file = await getFile(id, project.entryFilePath);
    entryContent = file?.content ?? "";
  }
  return projectToRecord(project, entryContent);
}

export async function saveProjectMeta(
  project: CustomIndicatorProject
): Promise<void> {
  const db = await openCustomIndicatorsDb();
  try {
    const files = await listFilesInDb(db, project.id);
    project.sizeBytes = computeProjectSizeBytes(files);
    project.entryContent = getEntryContent(files, project.entryFilePath);
    project.updatedAt = Date.now();
    await db.put(PROJECTS_STORE, project);
  } finally {
    db.close();
  }
}

/** Legacy adapter — saves full project from a flat record (entry file only) */
export async function saveCustomIndicator(
  record: CustomIndicatorRecord
): Promise<void> {
  const existing = await getProject(record.id);
  const now = Date.now();
  const project: CustomIndicatorProject = normalizeProject({
    id: record.id,
    name: record.name,
    version: record.version,
    description: existing?.description ?? "",
    overlay: record.overlay,
    tags: existing?.tags ?? ["custom", "csharp"],
    kind: existing?.kind ?? "indicator",
    entryFilePath: existing?.entryFilePath ?? DEFAULT_ENTRY_FILE,
    csprojFilePath: existing?.csprojFilePath ?? "Indicator.csproj",
    sizeBytes: existing?.sizeBytes ?? byteSize(record.code),
    entryContent: record.code,
    owner: existing?.owner ?? "user",
    source: existing?.source ?? "created",
    readOnly: existing?.readOnly ?? false,
    hasSource: existing?.hasSource ?? true,
    publisher: existing?.publisher,
    installedAt: existing?.installedAt,
    sourceTemplateId: existing?.sourceTemplateId,
    createdAt: record.createdAt,
    updatedAt: now,
  });

  await saveProjectMeta(project);

  const entryPath = project.entryFilePath;
  const files = existing ? await listFiles(record.id) : [];
  const entryExists = files.some((f) => f.path === entryPath);

  if (entryExists) {
    await writeFile(record.id, entryPath, record.code, "file");
  } else {
    await createNode(record.id, entryPath, "file", record.code);
  }
}

export async function deleteProject(id: string): Promise<void> {
  const db = await openCustomIndicatorsDb();
  try {
    const files = await listFilesInDb(db, id);
    for (const file of files) {
      await db.delete(FILES_STORE, file.id);
    }
    await db.delete(BUILDS_STORE, id);
    await db.delete(PROJECTS_STORE, id);
  } finally {
    db.close();
  }
}

// --- Build CRUD (persisted compiled assemblies) ---

export async function getBuild(
  projectId: string
): Promise<CustomIndicatorBuild | undefined> {
  const db = await openCustomIndicatorsDb();
  try {
    return db.get(BUILDS_STORE, projectId);
  } finally {
    db.close();
  }
}

export async function saveBuild(build: CustomIndicatorBuild): Promise<void> {
  const db = await openCustomIndicatorsDb();
  try {
    await db.put(BUILDS_STORE, build);
  } finally {
    db.close();
  }
}

export async function deleteBuild(projectId: string): Promise<void> {
  const db = await openCustomIndicatorsDb();
  try {
    await db.delete(BUILDS_STORE, projectId);
  } finally {
    db.close();
  }
}

export async function listBuilds(): Promise<CustomIndicatorBuild[]> {
  const db = await openCustomIndicatorsDb();
  try {
    return db.getAll(BUILDS_STORE);
  } finally {
    db.close();
  }
}

/** Legacy adapter */
export async function deleteCustomIndicator(id: string): Promise<void> {
  await deleteProject(id);
}

export async function createProject(
  name: string,
  version = "1.0"
): Promise<CustomIndicatorProject> {
  const id = createNewIndicatorId();
  const now = Date.now();
  const scaffold = getDefaultProjectFiles(name);
  let sizeBytes = 0;

  const project: CustomIndicatorProject = {
    id,
    name: name.trim() || "My Custom Indicator",
    version,
    description: "",
    overlay: true,
    kind: "indicator",
    tags: ["custom", "csharp", "draft"],
    entryFilePath: DEFAULT_ENTRY_FILE,
    csprojFilePath: "Indicator.csproj",
    sizeBytes: 0,
    entryContent: "",
    ...defaultUserProjectOwnership(),
    createdAt: now,
    updatedAt: now,
  };

  const db = await openCustomIndicatorsDb();
  try {
    for (const item of scaffold) {
      const path = normalizePath(item.path);
      const content = item.content;
      const nodeSize = item.type === "file" ? byteSize(content) : 0;
      sizeBytes += nodeSize;

      const node: ProjectFileNode = {
        id: fileId(id, path),
        projectId: id,
        path,
        type: item.type,
        content: item.type === "file" ? content : "",
        sizeBytes: nodeSize,
        createdAt: now,
        updatedAt: now,
      };
      await db.put(FILES_STORE, node);

      if (path === DEFAULT_ENTRY_FILE) {
        project.entryContent = content;
      }
    }

    project.sizeBytes = sizeBytes;
    await db.put(PROJECTS_STORE, project);
    return project;
  } finally {
    db.close();
  }
}

// --- File CRUD ---

async function listFilesInDb(
  db: CustomIndicatorsDb,
  projectId: string
): Promise<ProjectFileNode[]> {
  return db.getAllFromIndex(FILES_STORE, "byProject", projectId);
}

export async function listFiles(projectId: string): Promise<ProjectFileNode[]> {
  const db = await openCustomIndicatorsDb();
  try {
    const files = await listFilesInDb(db, projectId);
    files.sort((a, b) => a.path.localeCompare(b.path));
    return files;
  } finally {
    db.close();
  }
}

export async function getFile(
  projectId: string,
  path: string
): Promise<ProjectFileNode | undefined> {
  const db = await openCustomIndicatorsDb();
  try {
    return db.get(FILES_STORE, fileId(projectId, path));
  } finally {
    db.close();
  }
}

async function refreshProjectStats(
  db: CustomIndicatorsDb,
  projectId: string
): Promise<void> {
  const project = await db.get(PROJECTS_STORE, projectId);
  if (!project) return;

  const files = await listFilesInDb(db, projectId);
  project.sizeBytes = computeProjectSizeBytes(files);
  project.entryContent = getEntryContent(files, project.entryFilePath);
  project.updatedAt = Date.now();
  await db.put(PROJECTS_STORE, project);
}

export async function writeFile(
  projectId: string,
  path: string,
  content: string,
  type: "file" | "folder" = "file"
): Promise<void> {
  const normalized = normalizePath(path);
  const validation = validatePath(normalized);
  if (!validation.ok) {
    throw new Error(validation.error);
  }

  const db = await openCustomIndicatorsDb();
  try {
    const existingFiles = await listFilesInDb(db, projectId);
    const existing = existingFiles.find((f) => f.path === normalized);
    const limitResult = checkLimits({
      content: type === "file" ? content : "",
      existingFiles,
      targetPath: normalized,
      isNewNode: !existing,
    });
    if (!limitResult.ok) {
      throw new Error(limitResult.error);
    }

    const now = Date.now();
    const node: ProjectFileNode = {
      id: fileId(projectId, normalized),
      projectId,
      path: normalized,
      type,
      content: type === "file" ? content : "",
      sizeBytes: type === "file" ? byteSize(content) : 0,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };
    await db.put(FILES_STORE, node);
    await refreshProjectStats(db, projectId);
  } finally {
    db.close();
  }
}

export async function createNode(
  projectId: string,
  path: string,
  type: "file" | "folder",
  content = ""
): Promise<ProjectFileNode> {
  const normalized = normalizePath(path);
  const validation = validatePath(normalized);
  if (!validation.ok) {
    throw new Error(validation.error);
  }

  const db = await openCustomIndicatorsDb();
  try {
    const existingFiles = await listFilesInDb(db, projectId);
    if (existingFiles.some((f) => f.path === normalized)) {
      throw new Error(`Path already exists: ${normalized}`);
    }

    const limitResult = checkLimits({
      content: type === "file" ? content : "",
      existingFiles,
      isNewNode: true,
    });
    if (!limitResult.ok) {
      throw new Error(limitResult.error);
    }

    const now = Date.now();
    const node: ProjectFileNode = {
      id: fileId(projectId, normalized),
      projectId,
      path: normalized,
      type,
      content: type === "file" ? content : "",
      sizeBytes: type === "file" ? byteSize(content) : 0,
      createdAt: now,
      updatedAt: now,
    };
    await db.put(FILES_STORE, node);
    await refreshProjectStats(db, projectId);
    return node;
  } finally {
    db.close();
  }
}

export async function deleteNode(
  projectId: string,
  path: string
): Promise<void> {
  const normalized = normalizePath(path);
  const db = await openCustomIndicatorsDb();
  try {
    const files = await listFilesInDb(db, projectId);
    const pathsToDelete = collectDescendantPaths(normalized, files);
    for (const p of pathsToDelete) {
      await db.delete(FILES_STORE, fileId(projectId, p));
    }
    await refreshProjectStats(db, projectId);
  } finally {
    db.close();
  }
}

export async function renameNode(
  projectId: string,
  oldPath: string,
  newName: string
): Promise<void> {
  const normalizedOld = normalizePath(oldPath);
  const newPath = renamePath(normalizedOld, newName);
  const validation = validatePath(newPath);
  if (!validation.ok) {
    throw new Error(validation.error);
  }

  const db = await openCustomIndicatorsDb();
  try {
    const files = await listFilesInDb(db, projectId);
    if (files.some((f) => f.path === newPath && f.path !== normalizedOld)) {
      throw new Error(`Path already exists: ${newPath}`);
    }

    const affected = files.filter(
      (f) =>
        f.path === normalizedOld || f.path.startsWith(`${normalizedOld}/`)
    );

    const now = Date.now();
    for (const node of affected) {
      const suffix =
        node.path === normalizedOld
          ? newPath
          : joinPath(newPath, node.path.slice(normalizedOld.length + 1));
      await db.delete(FILES_STORE, node.id);
      const updated: ProjectFileNode = {
        ...node,
        id: fileId(projectId, suffix),
        path: suffix,
        updatedAt: now,
      };
      await db.put(FILES_STORE, updated);
    }

    const project = await db.get(PROJECTS_STORE, projectId);
    if (project?.entryFilePath === normalizedOld) {
      project.entryFilePath = newPath;
      await db.put(PROJECTS_STORE, project);
    } else if (project?.entryFilePath.startsWith(`${normalizedOld}/`)) {
      project.entryFilePath = joinPath(
        newPath,
        project.entryFilePath.slice(normalizedOld.length + 1)
      );
      await db.put(PROJECTS_STORE, project);
    }

    await refreshProjectStats(db, projectId);
  } finally {
    db.close();
  }
}

export async function moveNode(
  projectId: string,
  sourcePath: string,
  targetFolderPath: string
): Promise<void> {
  const normalizedSource = normalizePath(sourcePath);
  const normalizedTarget = normalizePath(targetFolderPath);
  const newPath = movePath(normalizedSource, normalizedTarget);

  if (normalizedTarget.startsWith(`${normalizedSource}/`)) {
    throw new Error("Cannot move a folder into itself");
  }

  const validation = validatePath(newPath);
  if (!validation.ok) {
    throw new Error(validation.error);
  }

  const db = await openCustomIndicatorsDb();
  try {
    const files = await listFilesInDb(db, projectId);
    if (files.some((f) => f.path === newPath)) {
      throw new Error(`Path already exists: ${newPath}`);
    }

    const affected = files.filter(
      (f) =>
        f.path === normalizedSource ||
        f.path.startsWith(`${normalizedSource}/`)
    );

    const now = Date.now();
    for (const node of affected) {
      const suffix =
        node.path === normalizedSource
          ? newPath
          : joinPath(newPath, node.path.slice(normalizedSource.length + 1));
      await db.delete(FILES_STORE, node.id);
      const updated: ProjectFileNode = {
        ...node,
        id: fileId(projectId, suffix),
        path: suffix,
        updatedAt: now,
      };
      await db.put(FILES_STORE, updated);
    }

    const project = await db.get(PROJECTS_STORE, projectId);
    if (project?.entryFilePath === normalizedSource) {
      project.entryFilePath = newPath;
      await db.put(PROJECTS_STORE, project);
    } else if (
      project?.entryFilePath.startsWith(`${normalizedSource}/`)
    ) {
      project.entryFilePath = joinPath(
        newPath,
        project.entryFilePath.slice(normalizedSource.length + 1)
      );
      await db.put(PROJECTS_STORE, project);
    }

    await refreshProjectStats(db, projectId);
  } finally {
    db.close();
  }
}

export async function saveProjectWithFiles(
  project: CustomIndicatorProject,
  fileUpdates: { path: string; content: string; type: "file" | "folder" }[]
): Promise<void> {
  const db = await openCustomIndicatorsDb();
  try {
    const existingFiles = await listFilesInDb(db, project.id);
    const now = Date.now();

    for (const update of fileUpdates) {
      const normalized = normalizePath(update.path);
      const existing = existingFiles.find((f) => f.path === normalized);
      const limitResult = checkLimits({
        content: update.type === "file" ? update.content : "",
        existingFiles,
        targetPath: normalized,
        isNewNode: !existing,
      });
      if (!limitResult.ok) {
        throw new Error(limitResult.error);
      }

      const node: ProjectFileNode = {
        id: fileId(project.id, normalized),
        projectId: project.id,
        path: normalized,
        type: update.type,
        content: update.type === "file" ? update.content : "",
        sizeBytes:
          update.type === "file" ? byteSize(update.content) : 0,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
      };
      await db.put(FILES_STORE, node);

      const idx = existingFiles.findIndex((f) => f.path === normalized);
      if (idx >= 0) {
        existingFiles[idx] = node;
      } else {
        existingFiles.push(node);
      }
    }

    project.sizeBytes = computeProjectSizeBytes(existingFiles);
    project.entryContent = getEntryContent(
      existingFiles,
      project.entryFilePath
    );
    project.updatedAt = now;
    await db.put(PROJECTS_STORE, project);
  } finally {
    db.close();
  }
}

export { basename, normalizePath, validatePath };
